import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import { AudienceTag } from "../components/Tags";
import { CTASection } from "../components/CTASection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ARTICLES = [
  {
    date: "2026-06-18", tag: "business",
    img: "https://images.pexels.com/photos/29352449/pexels-photo-29352449.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600",
    alt: "Tokyo skyline at dusk",
    title: "The First Ninety Days: What Canadian Firms Miss in Tokyo",
    excerpt: "The deal was signed in Toronto. It will be won or lost in a Shibuya meeting room — and most of the losing happens quietly, in the first quarter.",
  },
  {
    date: "2026-05-30", tag: "business",
    img: "https://images.pexels.com/photos/461428/pexels-photo-461428.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600",
    alt: "Matcha tea bowls and whisk on dark wood",
    title: "Kaizen Is a Language Before It Is a Method",
    excerpt: "When 改善 arrives in Canada as a poster on a wall, something essential has been left on the plane. On carrying meaning, not slogans.",
  },
  {
    date: "2026-05-02", tag: "individual",
    img: "https://images.pexels.com/photos/230477/pexels-photo-230477.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600",
    alt: "Tea being poured into a ceramic bowl",
    title: "Reading a Japanese 'Yes': A Field Note for Executives",
    excerpt: "Hai means 'I hear you.' Sometimes it also means yes. A short guide to the most expensive syllable in transpacific business.",
  },
];

const inputCls = "w-full bg-cream border border-sand rounded-sm px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-coral transition-colors duration-300";

export default function Insights() {
  const [seminars, setSeminars] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", company: "", seminar_id: "", attendees: 1 });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/seminars`).then((r) => setSeminars(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.seminar_id) {
      toast.error("Please choose a seminar.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/seminar-registration`, { ...form, attendees: Number(form.attendees) });
      toast.success("Registration received — we will confirm by email shortly.");
      setForm({ name: "", email: "", company: "", seminar_id: "", attendees: 1 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const selected = seminars.find((s) => s.id === form.seminar_id);

  return (
    <div data-testid="insights-page" className="bg-slatebg pt-16">
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pt-20 sm:pt-28 pb-14">
        <Reveal>
          <Overline>INSIGHTS</Overline>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight text-navy max-w-3xl">
            Notes from both sides of the Pacific.
          </h1>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pb-20">
        <div className="divide-y divide-slateborder border-y border-slateborder">
          {ARTICLES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.06}>
              <article data-testid={`article-${i}`} className="group py-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center cursor-pointer">
                <div className="md:col-span-3">
                  <div className="overflow-hidden rounded-sm border border-slateborder">
                    <img src={a.img} alt={a.alt} className="w-full h-36 object-cover transition-transform duration-700 group-hover:scale-[1.04]" loading="lazy" />
                  </div>
                </div>
                <div className="md:col-span-8">
                  <div className="flex items-center gap-3 mb-2">
                    <p className="font-mono text-xs text-charcoal/50">{a.date}</p>
                    <AudienceTag audience={a.tag} testId={`article-${i}-tag`} />
                  </div>
                  <h2 className="font-serif text-2xl sm:text-3xl text-navy group-hover:text-coral-dark transition-colors duration-300">
                    {a.title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/70 max-w-2xl">{a.excerpt}</p>
                </div>
                <div className="md:col-span-1 md:text-right">
                  <ArrowUpRight size={20} className="inline text-coral-dark transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="seminars" data-testid="seminars-section" className="relative overflow-hidden bg-navy text-cream-surface">
        <img
          src="https://images.pexels.com/photos/235985/pexels-photo-235985.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1600"
          alt="" aria-hidden="true" loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-navy/90" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-28">
          <Reveal>
            <Overline light>UPCOMING SEMINARS</Overline>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight max-w-2xl">
              Small rooms, senior people, real questions.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-14">
            <div className="space-y-px">
              {seminars.map((s, i) => (
                <Reveal key={s.id} delay={i * 0.06}>
                  <button
                    type="button"
                    data-testid={`seminar-option-${s.id}`}
                    onClick={() => setForm({ ...form, seminar_id: s.id })}
                    className={`w-full text-left p-6 border transition-colors duration-300 ${
                      form.seminar_id === s.id
                        ? "border-coral bg-navy-surface"
                        : "border-navy-border hover:border-[#3D517A]"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-4">
                      <h3 className="font-serif text-xl text-cream-surface">{s.title}</h3>
                      <span className="font-mono text-xs text-coral-light shrink-0">{s.date}</span>
                    </div>
                    <p className="mt-2 text-sm text-[#B7C6E2] leading-relaxed">{s.description}</p>
                    <p className="mt-2 text-xs font-mono tracking-widest text-silver">{s.location.toUpperCase()}</p>
                  </button>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="seminar-form" className="bg-navy-surface border border-navy-border rounded-sm p-8 space-y-4">
                <h3 className="font-serif text-2xl text-cream-surface">Register for a seminar</h3>
                <p className="text-sm text-[#B7C6E2]">
                  {selected ? `${selected.title} — ${selected.date} · ${selected.location}` : "Select a seminar on the left, then complete your details."}
                </p>
                <input data-testid="seminar-name" required placeholder="Full name" value={form.name} onChange={set("name")}
                  className={`${inputCls} !bg-navy !border-navy-border !text-cream-surface placeholder:!text-silver/60`} />
                <input data-testid="seminar-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")}
                  className={`${inputCls} !bg-navy !border-navy-border !text-cream-surface placeholder:!text-silver/60`} />
                <input data-testid="seminar-company" placeholder="Company (optional)" value={form.company} onChange={set("company")}
                  className={`${inputCls} !bg-navy !border-navy-border !text-cream-surface placeholder:!text-silver/60`} />
                <div>
                  <label className="block text-xs font-mono tracking-widest text-silver mb-2">NUMBER OF ATTENDEES</label>
                  <input data-testid="seminar-attendees" required type="number" min="1" max="20" value={form.attendees} onChange={set("attendees")}
                    className={`${inputCls} !bg-navy !border-navy-border !text-cream-surface`} />
                </div>
                <button
                  type="submit"
                  data-testid="seminar-submit"
                  disabled={sending}
                  className="w-full bg-coral hover:bg-coral-dark disabled:opacity-60 text-white font-medium px-6 py-3.5 rounded-full transition-colors duration-300"
                >
                  {sending ? "Registering…" : "Register"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
